import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Truck, Plus } from "lucide-react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import apiService from "../services/api";
import {
  getInventoryBySKU,
  allocateInventory,
  createInventoryItem,
} from "../services/api";
import { useRealTime } from "../context/RealTimeContext";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import Modal from "../components/ui/Modal";
import Button from "../components/ui/Button";

const CreateDeliveryOrder = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { triggerUpdate } = useRealTime();

  // Order data
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingDO, setCreatingDO] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState("order");

  // Delivery form fields
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [actualDeliveryDate, setActualDeliveryDate] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("standard");
  const [deliveryStatus, setDeliveryStatus] = useState("pending");

  // Customer Acknowledgment fields
  const [customerName, setCustomerName] = useState("");
  const [customerSignature, setCustomerSignature] = useState("");
  const [receiptDate, setReceiptDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [receiptTime, setReceiptTime] = useState(
    new Date().toTimeString().slice(0, 5)
  );
  const [additionalNotes, setAdditionalNotes] = useState("");

  // Inventory data
  const [inventoryData, setInventoryData] = useState({});
  const [loadingInventory, setLoadingInventory] = useState(false);

  // Insufficient stock warning modal state
  const [showInsufficientStockModal, setShowInsufficientStockModal] =
    useState(false);
  const [insufficientStockItems, setInsufficientStockItems] = useState([]);

  // Add stock modal state
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [selectedStockItem, setSelectedStockItem] = useState(null);
  const [stockForm, setStockForm] = useState({
    quantity: "",
    batch: "",
    location: "",
    supplierName: "",
    unitCost: "",
    expiryDate: "",
    notes: "",
  });
  const [addingStock, setAddingStock] = useState(false);

  const fetchInventoryData = useCallback(async (items) => {
    setLoadingInventory(true);
    try {
      const inventoryPromises = items.map(async (item) => {
        const productSkuId = item.productSkuId;
        const productId = item.productId;

        if (productSkuId) {
          try {
            const response = await getInventoryBySKU(productSkuId);
            return { productSkuId, productId, data: response };
          } catch (error) {
            console.error(
              `Failed to fetch inventory for SKU ${productSkuId}:`,
              error
            );
            return { productSkuId, productId, data: null };
          }
        } else if (productId) {
          try {
            const response = await apiService.getInventoryByProduct(productId);
            return { productSkuId: null, productId, data: response };
          } catch (error) {
            console.error(
              `Failed to fetch inventory for product ${productId}:`,
              error
            );
            return { productSkuId: null, productId, data: null };
          }
        }
        return { productSkuId: null, productId: null, data: null };
      });

      const results = await Promise.all(inventoryPromises);
      const inventoryMap = {};
      results.forEach(({ productSkuId, productId, data }) => {
        if (data) {
          if (productSkuId) {
            inventoryMap[productSkuId] = data;
          } else if (productId) {
            inventoryMap[productId] = data;
          }
        }
      });

      setInventoryData(inventoryMap);

      // Update delivery items with maxDelivery limits
      setDeliveryItems((prevItems) =>
        prevItems.map((item) => {
          const inventoryInfo =
            inventoryMap[item.productSkuId] || inventoryMap[item.productId];
          if (inventoryInfo) {
            const availableStock = inventoryInfo.availableStock || 0;
            const pendingQuantity = item.pendingQuantity || 0;
            const maxDelivery = Math.min(availableStock, pendingQuantity);
            return { ...item, maxDelivery, availableStock };
          }
          return {
            ...item,
            maxDelivery: item.pendingQuantity || 0,
            availableStock: 0,
          };
        })
      );
    } catch (error) {
      console.error("Error fetching inventory data:", error);
      toast.error("Failed to load inventory data");
    } finally {
      setLoadingInventory(false);
    }
  }, []);

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      const orderData = await apiService.getOrder(orderId);
      setOrder(orderData);

      // Calculate delivered quantities from all delivery orders
      const deliveredQuantitiesMap = {};
      if (orderData.deliveryOrders && orderData.deliveryOrders.length > 0) {
        orderData.orderItems.forEach((orderItem) => {
          let totalDelivered = 0;
          orderData.deliveryOrders.forEach((deliveryOrder) => {
            if (deliveryOrder.items) {
              deliveryOrder.items.forEach((deliveryItem) => {
                // Match by productSkuId or productId
                if (
                  deliveryItem.productSkuId === orderItem.productSkuId ||
                  deliveryItem.productId === orderItem.productId ||
                  deliveryItem.orderItem?.productSkuId ===
                    orderItem.productSkuId ||
                  deliveryItem.orderItem?.productId === orderItem.productId
                ) {
                  totalDelivered += deliveryItem.quantity || 0;
                }
              });
            }
          });
          deliveredQuantitiesMap[
            orderItem.productSkuId || orderItem.productId
          ] = totalDelivered;
        });
      }

      // Initialize delivery items from order items
      const items = orderData.orderItems.map((item) => {
        const itemKey = item.productSkuId || item.productId;
        const deliveredQty =
          deliveredQuantitiesMap[itemKey] || item.deliveredQuantity || 0;
        const pendingQty = item.quantity - deliveredQty;

        return {
          productId: item.productSku?.product?.id || item.productId,
          productSkuId: item.productSkuId,
          name: item.productSku?.product?.name || item.product?.name || "",
          description:
            item.productSku?.description ||
            item.productSku?.product?.description ||
            item.product?.description ||
            "",
          itemCode:
            item.productSku?.product?.itemCode || item.product?.itemCode || "",
          sku: item.productSku?.sku || item.product?.sku || "",
          quantity: item.quantity,
          deliveredQuantity: deliveredQty,
          pendingQuantity: Math.max(0, pendingQty),
          toDeliver: Math.max(0, pendingQty),
          deliverNow: 0,
          unitPrice: item.price || item.unitPrice || 0,
          maxDelivery: 0,
          unit: item.unit || "PCS",
        };
      });

      setDeliveryItems(items);
      setExpectedDeliveryDate(
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0]
      );
      setActualDeliveryDate(new Date().toISOString().split("T")[0]);

      // Fetch inventory data
      await fetchInventoryData(items);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to load order details");
      navigate("/admin/orders");
    } finally {
      setLoading(false);
    }
  }, [orderId, navigate, fetchInventoryData]);

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId, fetchOrderDetails]);

  const getInventoryInfo = (productId, productSkuId) => {
    const data =
      inventoryData[productSkuId] ||
      (productId ? inventoryData[productId] : null);
    if (!data)
      return {
        available: "Loading...",
        total: "Loading...",
        reserved: "Loading...",
      };
    return {
      available: data.availableStock,
      total: data.totalStock,
      reserved: data.totalReserved,
    };
  };

  const getBatchInfo = (productId, productSkuId) => {
    const data =
      inventoryData[productSkuId] ||
      (productId ? inventoryData[productId] : null);
    if (!data || !data.items) return [];

    const batchMap = {};
    data.items.forEach((item) => {
      const batchKey = item.batch || item.batchNumber || "No Batch";
      const availableQty = item.quantity - item.reservedQuantity;

      if (availableQty > 0) {
        if (!batchMap[batchKey]) {
          batchMap[batchKey] = {
            batch: batchKey,
            available: 0,
            total: 0,
            reserved: 0,
            expiryDate: item.expiryDate,
            dateReceived: item.dateReceived,
            location: item.location,
          };
        }
        batchMap[batchKey].available += availableQty;
        batchMap[batchKey].total += item.quantity;
        batchMap[batchKey].reserved += item.reservedQuantity;
      }
    });

    const batches = Object.values(batchMap).sort((a, b) => {
      if (a.expiryDate && b.expiryDate) {
        return new Date(a.expiryDate) - new Date(b.expiryDate);
      }
      if (a.dateReceived && b.dateReceived) {
        return new Date(a.dateReceived) - new Date(b.dateReceived);
      }
      return 0;
    });

    return batches;
  };

  const getSelectedBatchInfo = (productId, productSkuId) => {
    const batches = getBatchInfo(productId, productSkuId);
    if (batches.length === 0) return "No batch info";

    const selectedBatch = batches[0];
    let info = `Batch: ${selectedBatch.batch}`;
    if (selectedBatch.expiryDate) {
      info += ` | Exp: ${new Date(
        selectedBatch.expiryDate
      ).toLocaleDateString()}`;
    }
    return info;
  };

  const selectBatchesForDelivery = (batches, requiredQuantity) => {
    if (batches.length === 0 || requiredQuantity <= 0) return [];

    const selectedBatches = [];
    let remainingQuantity = requiredQuantity;

    for (const batch of batches) {
      if (remainingQuantity <= 0) break;

      const availableFromBatch = Math.min(batch.available, remainingQuantity);

      if (availableFromBatch > 0) {
        selectedBatches.push({
          ...batch,
          allocatedQuantity: availableFromBatch,
        });
        remainingQuantity -= availableFromBatch;
      }
    }

    return selectedBatches;
  };

  const getBarcode = (item) => {
    return (
      item.barcode ||
      item.sku ||
      item.itemCode ||
      `SKU-${item.productSkuId || item.productId}`
    );
  };

  const handleDeliveryItemChange = (index, field, value) => {
    setDeliveryItems((prevItems) => {
      const newItems = [...prevItems];
      newItems[index] = { ...newItems[index], [field]: parseInt(value) || 0 };
      return newItems;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const selectedItems = deliveryItems.filter((item) => item.deliverNow > 0);

    if (selectedItems.length === 0) {
      toast.error("Please enter quantity to deliver for at least one item");
      return;
    }

    if (!actualDeliveryDate) {
      toast.error("Please select an actual delivery date");
      return;
    }

    // Validate that quantities don't exceed pending quantities
    const overDeliveryItems = selectedItems.filter(
      (item) => item.deliverNow > item.quantity - (item.deliveredQuantity || 0)
    );
    if (overDeliveryItems.length > 0) {
      toast.error("Some items exceed the remaining quantity to be delivered.");
      return;
    }

    // Check for stock issues - only show warning if user is trying to deliver more than available
    const itemsWithStockIssues = selectedItems.filter(
      (item) => item.deliverNow > item.maxDelivery
    );
    if (itemsWithStockIssues.length > 0) {
      // Show modal instead of window.confirm
      setInsufficientStockItems(itemsWithStockIssues);
      setShowInsufficientStockModal(true);
      return;
    }

    // Proceed with DO creation
    await createDeliveryOrder(selectedItems);
  };

  const createDeliveryOrder = async (selectedItems) => {
    setCreatingDO(true);
    try {
      const deliveryOrderData = {
        orderId: order.id,
        // deliveryNumber: `DO-${Date.now()}-${Math.floor(Math.random() * 1000)}`, // Let backend generate this
        deliveryStatus: deliveryStatus,
        expectedDeliveryDate: new Date(expectedDeliveryDate),
        actualDeliveryDate: new Date(actualDeliveryDate),
        deliveryMethod: deliveryMethod,
        deliveryAddress: {
          businessName: order?.customer?.businessName,
          addressLine: order?.customer?.shipAddressLine,
          district: order?.customer?.shipDistrict,
          state: order?.customer?.shipState,
          pinCode: order?.customer?.shipPinCode,
          country: order?.customer?.shipCountry,
          gpsLocation: order?.customer?.shipGpsLocation,
        },
        contactPerson: order?.customer?.contactName || "N/A",
        contactPhone: order?.customer?.contactMobile || "N/A",
        deliveryInstructions: deliveryNotes,
        customerAcknowledgment: {
          customerName,
          customerSignature,
          receiptDate,
          receiptTime,
          additionalNotes,
        },
        items: selectedItems.map((item) => {
          const batchInfo = getBatchInfo(item.productId, item.productSkuId);
          const selectedBatches = selectBatchesForDelivery(
            batchInfo,
            item.deliverNow
          );
          const primaryBatch =
            selectedBatches.length > 0 ? selectedBatches[0] : null;

          return {
            productSkuId: item.productSkuId,
            quantity: item.deliverNow,
            productName: item.name,
            description: item.description,
            sku: item.sku || item.productId,
            unit: item.unit || "PCS",
            batch: primaryBatch?.batch || "N/A",
            batchNumber: primaryBatch?.batch || "N/A",
            barcode: getBarcode(item),
            expiryDate: primaryBatch?.expiryDate,
            location: primaryBatch?.location,
            selectedBatches: selectedBatches,
          };
        }),
      };

      const createdDO = await apiService.createDeliveryOrder(deliveryOrderData);

      // Allocate inventory
      try {
        const allocationItems = selectedItems.map((item) => ({
          productSkuId: item.productSkuId || item.productId,
          quantity: item.deliverNow,
        }));

        const allocationResult = await allocateInventory(
          allocationItems,
          createdDO.id
        );

        if (allocationResult.success) {
          toast.success("Inventory allocated successfully");
        } else {
          const failedItems = allocationResult.results.filter(
            (r) => !r.success
          );
          const failedNames = failedItems.map((f) => f.productSkuId).join(", ");
          toast.warning(
            `Delivery order created but some inventory allocation failed: ${failedNames}`
          );
        }
      } catch (allocationError) {
        console.error("Inventory allocation error:", allocationError);
        toast.warning("Delivery order created but inventory allocation failed");
      }

      toast.success("Delivery Order created successfully");

      // Trigger real-time updates
      triggerUpdate("deliveryOrderCreated", {
        deliveryOrder: createdDO,
        order,
      });

      // Navigate back to orders
      navigate("/admin/orders");
    } catch (error) {
      console.error("Error creating delivery order:", error);
      toast.error("Failed to create delivery order");
    } finally {
      setCreatingDO(false);
    }
  };

  const handleConfirmInsufficientStock = async () => {
    setShowInsufficientStockModal(false);
    // Proceed with creating delivery order with available stock
    const selectedItems = deliveryItems
      .filter((item) => item.deliverNow > 0)
      .map((item) => ({
        ...item,
        deliverNow: Math.min(
          item.deliverNow,
          item.maxDelivery || item.deliverNow
        ),
      }));
    await createDeliveryOrder(selectedItems);
    setInsufficientStockItems([]);
  };

  const handleCancelInsufficientStock = () => {
    setShowInsufficientStockModal(false);
    setInsufficientStockItems([]);
  };

  // Add stock handlers
  const handleOpenAddStock = (item) => {
    setSelectedStockItem(item);
    setStockForm({
      quantity: "",
      batch: "",
      location: "",
      supplierName: "",
      unitCost: "",
      expiryDate: "",
      notes: "",
    });
    setShowAddStockModal(true);
  };

  const handleCloseAddStock = () => {
    setShowAddStockModal(false);
    setSelectedStockItem(null);
    setStockForm({
      quantity: "",
      batch: "",
      location: "",
      supplierName: "",
      unitCost: "",
      expiryDate: "",
      notes: "",
    });
  };

  const handleStockFormChange = (field, value) => {
    setStockForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddStock = async () => {
    if (
      !selectedStockItem ||
      !stockForm.quantity ||
      Number(stockForm.quantity) <= 0
    ) {
      toast.error("Please enter a valid quantity");
      return;
    }

    try {
      setAddingStock(true);

      // Prepare inventory data
      const inventoryData = {
        productSkuId: selectedStockItem.productSkuId,
        quantity: Number(stockForm.quantity),
        batch: stockForm.batch || null,
        location: stockForm.location || null,
        supplierName: stockForm.supplierName || null,
        unitCost: stockForm.unitCost ? Number(stockForm.unitCost) : null,
        expiryDate: stockForm.expiryDate
          ? new Date(stockForm.expiryDate).toISOString()
          : null,
        notes: stockForm.notes || null,
      };

      await createInventoryItem(inventoryData);
      toast.success(`Added ${stockForm.quantity} units to inventory`);

      // Refresh inventory data
      await fetchInventoryData(deliveryItems);

      handleCloseAddStock();
    } catch (error) {
      console.error("Error adding stock:", error);
      toast.error(error.message || "Failed to add stock");
    } finally {
      setAddingStock(false);
    }
  };

  const tabs = [
    { id: "order", label: "Order Info", icon: "📋" },
    { id: "schedule", label: "Schedule", icon: "📅" },
    { id: "items", label: "Delivery Items", icon: "📦" },
    { id: "acknowledgment", label: "Acknowledgment", icon: "✅" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">Order not found</div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4">
      <div className="w-full">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/admin/orders")}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Orders
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Truck className="w-8 h-8 text-green-600" />
                Create Delivery Order
              </h1>
              <p className="mt-2 text-gray-600">
                Create delivery order for PO:{" "}
                <span className="font-semibold text-blue-600">
                  {order?.poNumber || order?.orderNumber || `#${order?.id}`}
                </span>
              </p>
            </div>
            <div className="text-right bg-green-50 px-4 py-2 rounded-lg border border-green-200">
              <div className="text-xs text-green-600 font-medium">
                DO Number
              </div>
              <div className="text-lg font-mono font-bold text-green-700">
                Auto-generated
              </div>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Tab Navigation */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="flex space-x-1 p-4 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-white text-green-600 shadow-sm border border-green-200"
                      : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <form
            onSubmit={handleSubmit}
            className="p-6 max-h-[70vh] overflow-y-auto"
          >
            {/* Order Info Tab */}
            {activeTab === "order" && (
              <div className="space-y-6">
                {/* Order Reference */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    Purchase Order Reference
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Order ID
                      </label>
                      <input
                        type="text"
                        value={`#${order?.id || ""}`}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        PO Number
                      </label>
                      <input
                        type="text"
                        value={order?.poNumber || order?.orderNumber || "N/A"}
                        disabled
                        className="w-full border border-blue-300 rounded-lg px-3 py-2 bg-blue-50 text-sm font-semibold"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Order Date
                      </label>
                      <input
                        type="text"
                        value={
                          order?.orderDate
                            ? format(new Date(order.orderDate), "dd/MM/yyyy")
                            : "N/A"
                        }
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Order Value
                      </label>
                      <input
                        type="text"
                        value={`₹${
                          order?.totalAmount?.toLocaleString() || "0"
                        }`}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Order Status
                      </label>
                      <input
                        type="text"
                        value={order?.status?.toUpperCase() || "N/A"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Order Type
                      </label>
                      <input
                        type="text"
                        value={order?.orderType || "Standard Order"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Customer Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Business Name
                      </label>
                      <input
                        type="text"
                        value={order?.customer?.businessName || "N/A"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Contact Person
                      </label>
                      <input
                        type="text"
                        value={order?.customer?.contactName || "N/A"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Mobile
                      </label>
                      <input
                        type="text"
                        value={order?.customer?.contactMobile || "N/A"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="text"
                        value={order?.customer?.email || "N/A"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        GST Number
                      </label>
                      <input
                        type="text"
                        value={order?.customer?.gstNumber || "N/A"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Business Type
                      </label>
                      <input
                        type="text"
                        value={order?.customer?.businessType || "N/A"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Delivery Address */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                    Delivery Address
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Address Line
                      </label>
                      <input
                        type="text"
                        value={order?.customer?.shipAddressLine || "N/A"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        District
                      </label>
                      <input
                        type="text"
                        value={order?.customer?.shipDistrict || "N/A"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        State
                      </label>
                      <input
                        type="text"
                        value={order?.customer?.shipState || "N/A"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Pin Code
                      </label>
                      <input
                        type="text"
                        value={order?.customer?.shipPinCode || "N/A"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Country
                      </label>
                      <input
                        type="text"
                        value={order?.customer?.shipCountry || "N/A"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Schedule Tab */}
            {activeTab === "schedule" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                    Delivery Schedule
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Expected Delivery Date *
                      </label>
                      <input
                        type="date"
                        value={expectedDeliveryDate}
                        onChange={(e) =>
                          setExpectedDeliveryDate(e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Actual Delivery Date *
                      </label>
                      <input
                        type="date"
                        value={actualDeliveryDate}
                        onChange={(e) => setActualDeliveryDate(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Delivery Method
                      </label>
                      <select
                        value={deliveryMethod}
                        onChange={(e) => setDeliveryMethod(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="standard">Standard Delivery</option>
                        <option value="express">Express Delivery</option>
                        <option value="overnight">Overnight Delivery</option>
                        <option value="scheduled">Scheduled Delivery</option>
                        <option value="pickup">Customer Pickup</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Delivery Status
                      </label>
                      <select
                        value={deliveryStatus}
                        onChange={(e) => setDeliveryStatus(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="dispatched">Dispatched</option>
                        <option value="in_transit">In Transit</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    Delivery Instructions
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Customer's Preferred Method
                      </label>
                      <input
                        type="text"
                        value={
                          order?.customer?.preferredDeliveryMethod || "Standard"
                        }
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Special Instructions
                      </label>
                      <input
                        type="text"
                        value={order?.customer?.specialInstructions || "None"}
                        disabled
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-sm"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Additional Delivery Notes
                    </label>
                    <textarea
                      value={deliveryNotes}
                      onChange={(e) => setDeliveryNotes(e.target.value)}
                      rows="3"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Add any specific delivery instructions, contact details, or special handling requirements..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Items Tab */}
            {activeTab === "items" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                    Items for Delivery
                    {order?.poNumber && (
                      <span className="ml-3 text-sm font-normal text-gray-500">
                        PO:{" "}
                        <span className="text-blue-600 font-medium">
                          {order.poNumber}
                        </span>
                      </span>
                    )}
                  </h3>
                  <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                    <table className="w-full min-w-[900px]">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Product
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Barcode
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Ordered
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Delivered
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Available
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Deliver Now *
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Unit
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {deliveryItems.map((item, index) => {
                          const inventoryInfo = getInventoryInfo(
                            item.productId,
                            item.productSkuId
                          );
                          const barcode = getBarcode(item);

                          return (
                            <tr
                              key={`delivery-item-${index}`}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">
                                  {item.name}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {item.description || "No description"}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="font-mono text-sm">
                                  {barcode}
                                </div>
                                {loadingInventory ? (
                                  <div className="text-xs text-gray-400">
                                    Loading...
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {getSelectedBatchInfo(
                                      item.productId,
                                      item.productSkuId
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {item.quantity}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {item.deliveredQuantity || 0}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {loadingInventory ? (
                                  <div className="text-xs text-gray-400">
                                    Loading...
                                  </div>
                                ) : (
                                  <div>
                                    <div
                                      className={`font-bold ${
                                        inventoryInfo.available <
                                        item.pendingQuantity
                                          ? "text-red-600"
                                          : "text-green-600"
                                      }`}
                                    >
                                      {inventoryInfo.available}
                                    </div>
                                    {inventoryInfo.available <
                                      item.pendingQuantity && (
                                      <div className="flex flex-col items-center gap-1 mt-1">
                                        <div className="text-xs text-red-500">
                                          Low stock
                                        </div>
                                        <button
                                          onClick={() =>
                                            handleOpenAddStock(item)
                                          }
                                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                                          title="Add stock for this item"
                                        >
                                          <Plus className="w-3 h-3" />
                                          Add Stock
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex flex-col items-center">
                                  <input
                                    type="number"
                                    min="0"
                                    max={item.maxDelivery}
                                    value={item.deliverNow}
                                    onChange={(e) =>
                                      handleDeliveryItemChange(
                                        index,
                                        "deliverNow",
                                        e.target.value
                                      )
                                    }
                                    className={`w-20 border rounded-lg px-2 py-1.5 text-center text-sm font-medium focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                                      item.deliverNow > item.maxDelivery
                                        ? "border-red-500 bg-red-50"
                                        : "border-gray-300"
                                    }`}
                                    placeholder="0"
                                  />
                                  <div className="text-xs text-gray-500 mt-1">
                                    Max: {item.maxDelivery}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center text-sm text-gray-600">
                                {item.unit || "PCS"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-green-50">
                        <tr>
                          <td
                            colSpan="5"
                            className="px-4 py-3 text-sm font-medium text-gray-700"
                          >
                            Total Items for Delivery:{" "}
                            <span className="text-green-600 font-bold">
                              {deliveryItems.reduce(
                                (sum, item) => sum + (item.deliverNow || 0),
                                0
                              )}
                            </span>{" "}
                            units
                          </td>
                          <td className="px-4 py-3 text-center text-sm font-bold text-green-600">
                            {deliveryItems.reduce(
                              (sum, item) => sum + (item.deliverNow || 0),
                              0
                            )}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {deliveryItems.some(
                    (item) => item.maxDelivery < item.pendingQuantity
                  ) && (
                    <div className="mt-3 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                      ⚠️ Some items have insufficient stock. Maximum available
                      quantities will be used.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Acknowledgment Tab */}
            {activeTab === "acknowledgment" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-rose-500 rounded-full mr-2"></span>
                    Customer Acknowledgment & Receipt Confirmation
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-gray-700">
                      <strong>DELIVERY ACKNOWLEDGMENT STATEMENT:</strong>
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      I hereby acknowledge that I have received the
                      above-mentioned goods in good condition and in the
                      quantities specified. The delivered items have been
                      inspected and found to be in accordance with the order
                      specifications. Any discrepancies or damages should be
                      reported within 24 hours of delivery.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Customer Name (Print)
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Print customer name clearly"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Customer Signature
                      </label>
                      <input
                        type="text"
                        value={customerSignature}
                        onChange={(e) => setCustomerSignature(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Digital signature or initials"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Date of Receipt
                      </label>
                      <input
                        type="date"
                        value={receiptDate}
                        onChange={(e) => setReceiptDate(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Time of Receipt
                      </label>
                      <input
                        type="time"
                        value={receiptTime}
                        onChange={(e) => setReceiptTime(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Additional Notes/Comments
                    </label>
                    <textarea
                      value={additionalNotes}
                      onChange={(e) => setAdditionalNotes(e.target.value)}
                      rows="3"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Any additional comments about the delivery condition or special notes..."
                    />
                  </div>
                </div>
              </div>
            )}
          </form>

          {/* Footer Actions */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Items to Deliver:</span>{" "}
                {deliveryItems.filter((item) => item.deliverNow > 0).length}{" "}
                products | <span className="font-medium">Total Quantity:</span>{" "}
                {deliveryItems.reduce(
                  (sum, item) => sum + (parseInt(item.deliverNow) || 0),
                  0
                )}{" "}
                units
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/admin/orders")}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={creatingDO}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {creatingDO ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Truck className="w-4 h-4" />
                      Create Delivery Order
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Insufficient Stock Warning Modal */}
      <Modal
        isOpen={showInsufficientStockModal}
        onClose={handleCancelInsufficientStock}
        title="⚠️ Insufficient Stock Warning"
      >
        <div className="space-y-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <p className="text-amber-800 dark:text-amber-200 text-sm font-medium mb-3">
              Some items have insufficient stock. The delivery will be limited
              to available stock.
            </p>

            {insufficientStockItems.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Items with insufficient stock:
                </p>
                <ul className="space-y-2">
                  {insufficientStockItems.map((item, index) => (
                    <li
                      key={index}
                      className="bg-white dark:bg-slate-800 rounded p-3 border border-gray-200 dark:border-slate-700"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {item.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            SKU: {item.sku || "N/A"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Requested:{" "}
                            <span className="font-semibold">
                              {item.pendingQuantity}
                            </span>
                          </p>
                          <p className="text-sm text-amber-600 dark:text-amber-400">
                            Available:{" "}
                            <span className="font-semibold">
                              {item.maxDelivery}
                            </span>
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              <strong>Note:</strong> The system will automatically adjust
              quantities to match available stock. You can create additional
              delivery orders later when stock is replenished.
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            variant="secondary"
            onClick={handleCancelInsufficientStock}
            style={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmInsufficientStock}
            style={{ flex: 1 }}
            disabled={creatingDO}
          >
            {creatingDO ? "Creating..." : "Continue with Available Stock"}
          </Button>
        </div>
      </Modal>

      {/* Add Stock Modal */}
      <Modal
        isOpen={showAddStockModal}
        onClose={handleCloseAddStock}
        title="Add Stock"
      >
        {selectedStockItem && (
          <div className="space-y-4">
            {/* Product Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                {selectedStockItem.name}
              </h4>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <p>SKU: {selectedStockItem.sku || "N/A"}</p>
                <p>Barcode: {getBarcode(selectedStockItem)}</p>
                <p className="text-red-600 dark:text-red-400 font-medium">
                  Current Available:{" "}
                  {
                    getInventoryInfo(
                      selectedStockItem.productId,
                      selectedStockItem.productSkuId
                    ).available
                  }
                </p>
                <p>Pending Quantity: {selectedStockItem.pendingQuantity}</p>
              </div>
            </div>

            {/* Quantity - Required */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Quantity to Add <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={stockForm.quantity}
                onChange={(e) =>
                  handleStockFormChange("quantity", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                placeholder="Enter quantity"
                required
              />
            </div>

            {/* Optional Fields in Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Batch Number
                </label>
                <input
                  type="text"
                  value={stockForm.batch}
                  onChange={(e) =>
                    handleStockFormChange("batch", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={stockForm.location}
                  onChange={(e) =>
                    handleStockFormChange("location", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Supplier Name
                </label>
                <input
                  type="text"
                  value={stockForm.supplierName}
                  onChange={(e) =>
                    handleStockFormChange("supplierName", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Unit Cost
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={stockForm.unitCost}
                  onChange={(e) =>
                    handleStockFormChange("unitCost", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  placeholder="Optional"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expiry Date
                </label>
                <input
                  type="date"
                  value={stockForm.expiryDate}
                  onChange={(e) =>
                    handleStockFormChange("expiryDate", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes
              </label>
              <textarea
                value={stockForm.notes}
                onChange={(e) => handleStockFormChange("notes", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                rows="3"
                placeholder="Optional notes"
              />
            </div>

            {/* Info Message */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-green-800 dark:text-green-200 text-sm">
                <strong>Note:</strong> Stock will be added immediately and the
                available quantity will be updated. You can then proceed with
                the delivery.
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <Button
            variant="secondary"
            onClick={handleCloseAddStock}
            style={{ flex: 1 }}
            disabled={addingStock}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddStock}
            style={{ flex: 1 }}
            disabled={
              addingStock ||
              !stockForm.quantity ||
              Number(stockForm.quantity) <= 0
            }
          >
            {addingStock ? "Adding..." : "Add Stock"}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default CreateDeliveryOrder;
